import * as Sentry from '@sentry/vue'
import { ToolsClass } from '@/base/class/tools.class'
import {
  isBrowserExtensionError,
  isClick2PaySdkRejection,
  isInjectedDocumentScriptError,
} from '@/base/utils/sentryFilters'

/**
 * Komunikaty wyciszane po TREŚCI. Wszystko, czego nie da się rozpoznać po komunikacie, idzie przez
 * `beforeSend` i predykaty z `sentryFilters.js`.
 */
export const SENTRY_IGNORE_ERRORS = [
  // znany szum z odmontowywania komponentów - nie jest błędem użytkownika
  /__disposer/,

  // Click to Pay: gdy SDK się nie zainicjuje, funkcjonalność sama się wyłącza
  // (`SET_C2P_IS_PROCESS_AVAILABLE` = false), a przyczyna idzie do własnego kanału logów
  // (`logC2PTransaction`). Źródłowy wyciek naprawiony w `store/modules/c2p.js`; te wzorce
  // zostają na to, czego z naszego kodu nie da się złapać - timeouty rzucane WEWNĄTRZ
  // `srcsdk.mastercard.js` i widgetu Visa SRC.
  /Window did not load after \d+ms/,
  /(C2P (Mastercard|Visa) SDK load timeout|Error loading C2P (Mastercard|Visa) SDK)/,
  /Failed to communicate with the iframe/,
  // SDK Mastercard nie doczekało się potwierdzenia `postMessage` do swojego iframe'a. Ta sama
  // historia: rzut powstaje wewnątrz `srcsdk.mastercard.js`, a diagnostyka idzie do
  // `logC2PTransaction`. 92 zdarzenia na dobę (pomiar 13.08.2026).
  /No ack for postMessage mastercard\./,

  // Znany, nieszkodliwy komunikat przeglądarki - nie odpowiada mu żadna awaria.
  /ResizeObserver loop/,

  // ⚠️ Kod WSTRZYKNIĘTY w stronę przez rozszerzenia i przeglądarki wbudowane w aplikacje.
  // Rozszerzenie wykonuje swój skrypt w kontekście dokumentu, więc jego `ReferenceError`-y
  // łapie `window.onerror` Sentry i wyglądają jak nasze. Rozpoznanie po nazwie jest tu pewne:
  // ŻADEN z tych identyfikatorów nie występuje w repozytorium (sprawdzone grepem 13.08.2026),
  // a stos ma klatki `<anonymous>` albo adres dokumentu - `isBrowserExtensionError` ich nie
  // złapie, bo rozszerzenie nie zostawia w stosie własnego pliku. Razem ~104 zdarzenia na dobę.
  /(jQuery is not defined|Can't find variable: jQuery)/,
  /Identifier 'CURRENCY_LIST' has already been declared/,
  /Can't find variable: ProductPageDetector/,
  /HTMLOUT is not defined/,
  // Warstwa blokowania treści wstrzykiwana przez Firefoksa na iOS. Pomiar 17.08.2026: 23 zdarzenia
  // na dobę, `browser.name: Firefox iOS` w 139/139, a cały stos to samo `forEach([native code])` -
  // ani jednej klatki z plikiem, więc `isBrowserExtensionError` nie ma się czego chwycić.
  // W repozytorium nie ma takiej destrukturyzacji (sprawdzone grepem `{ rule }` 17.08.2026).
  /Cannot destructure property 'rule' from null or undefined/,

  // ⚠️ Most natywny in-app browsera (`sendDataToNative`), NIE nasz `MobileAppClass`.
  // Ten komunikat znaczy, że `window.webkit` jest `undefined` - a nasze dwa jedyne odczyty
  // (`tools.class.js` → `isIOS`, `mobileApp.class.js`) są za pełnym łańcuchem strażników
  // `window.webkit && window.webkit.messageHandlers && …`, więc z naszego kodu paść nie może.
  // Zdejmując któryś z tych strażników, zdejmij też ten wzorzec. 35 zdarzeń na dobę.
  /evaluating 'window\.webkit\.messageHandlers'/,

  // Komunikaty pochodzące wprost z API rozszerzeń przeglądarki - powstają, gdy rozszerzenie
  // gada ze swoim workerem, a nie gdy dzieje się cokolwiek na bramce.
  /Invalid call to runtime\.sendMessage/,
  /Object Not Found Matching Id/,

  // ⚠️ AWARIE TRANSPORTU - ten sam stan w trzech różnych komunikatach: `Load failed` (Safari/WebKit),
  // `Failed to fetch` (Chrome/V8), `NetworkError when attempting to fetch resource.` (Firefox).
  // Powstają, gdy żądanie nie doszło do serwera - najczęściej przy opuszczaniu strony, przy zerwanym
  // łączu mobilnym i przy uśpionej karcie. Nie mówią nic o bramce ani o backendzie: odmowę backendu
  // widzimy osobno i dokładniej jako `Error: Bad Request` z `httpStatus` (~260 zdarzeń na dobę,
  // `flow: payment-update` w 901/961 - to jest sygnał actionable).
  //
  // Do 18.08.2026 wyciszony był tylko wariant Safari, więc ta sama awaria była raportowana albo nie,
  // zależnie od przeglądarki użytkownika. Pomiar 18.08.2026: 186 zdarzeń na dobę w 13 grupach
  // (`flow: payment-update` 125, `google-pay` 10, resztę stanowiły odrzucenia bez `.catch`
  // na ścieżkach reklamowych - te NAPRAWIONE w kodzie, nie wyciszone).
  //
  // Wszystkie trzy wzorce są ZAKOTWICZONE, żeby nie połknąć komunikatów, które tylko zawierają
  // ten tekst - w szczególności `TypeError: Failed to fetch dynamically imported module: …`,
  // czyli padnięty import chunka, który jest realnym problemem WebKita
  // (`doc/robocze/webkit-import-chunkow.md`) i musi być widoczny.
  //
  // Świadomy koszt: skali awarii sieci u użytkowników nie zmierzymy z Sentry.
  /^Load failed$/,
  /^Failed to fetch$/,
  /^NetworkError when attempting to fetch resource\.$/,
]

/**
 * Adresy, spod których błędy NIE są nasze, choć rzut następuje w kontekście naszej strony.
 * `denyUrls` porównuje adres z OSTATNIEJ sensownej klatki stosu (SDK 6 pomija `<anonymous>`
 * i `[native code]`), czyli z miejsca, gdzie realnie doszło do rzutu.
 *
 * - `yourcx.min.js` - widget ankietowy wpięty w `public/index.html`. Jego `onMessage` woła
 *   `JSON.parse` na KAŻDEJ wiadomości `postMessage` docierającej do okna, więc wysypuje się na
 *   komunikatach innych skryptów: `JSON Parse error: Unexpected identifier "<token>"`,
 *   21 zdarzeń na dobę, iOS w 189/195 (pomiar 18.08.2026). `ignoreErrors` tu nie zadziała -
 *   token w komunikacie jest zmienny, więc każda wiadomość zakłada w Sentry nową grupę.
 *   Filtry strukturalne też nie: `yourcx.min.js` JEST plikiem `.js` pobranym do strony.
 */
export const SENTRY_DENY_URLS = [/yourcx\.min\.js/]

/**
 * `ignoreErrors` dopasowuje się do KOMUNIKATU, a te trzy filtry muszą patrzeć głębiej: odrzucenia
 * z SDK Click to Pay mają komunikat generyczny (payload w `hint`), a błędów kodu wstrzykniętego
 * w dokument przez rozszerzenia i in-app browsery jest tyle, ile rozszerzeń - ich komunikaty są
 * dowolne i zmieniają się z każdą wersją. Wszystkie trzy rozpoznajemy więc po strukturze zdarzenia.
 * Szczegóły: `sentryFilters.js`.
 *
 * @param {object} event zdarzenie Sentry
 * @param {object} hint kontekst zdarzenia (m.in. `originalException`)
 * @returns {object|null} zdarzenie do wysłania albo `null`, gdy ma zostać wyciszone
 */
export function beforeSend(event, hint) {
  if (isClick2PaySdkRejection(hint?.originalException)) {
    return null
  }
  if (isInjectedDocumentScriptError(event)) {
    return null
  }
  if (isBrowserExtensionError(event)) {
    return null
  }
  return event
}

/**
 * Konfiguruje Sentry dla bramki. Woływane raz, z `context-init.js`, WYŁĄCZNIE na produkcji -
 * poza nią SDK nie wstaje w ogóle, a `reportError`/`reportMessage` (`errorReporting.js`) są no-opem.
 *
 * SDK jest CELOWO przypięte do 6.19.7 (EOL). Self-hostowany Sentry na error.blue.pl to instancja
 * sprzed envelope'ów - ma /api/<id>/store/ i /api/<id>/security/, nie ma /api/<id>/envelope/.
 * SDK 7 wysyła wyłącznie envelope'ami, więc wszystko kończyło się 403 bez nagłówków CORS i do
 * Sentry nie docierało NIC. SDK 6 wybiera endpoint per typ zdarzenia
 * (@sentry/core/esm/request.js: `useEnvelope = eventType === 'transaction' || !!api.tunnel`):
 *   - błędy      -> /store/    ✓ jedyne, co ta instancja przyjmie
 *   - transakcje -> /envelope/ ✗ stąd brak tracingu (Sentry 9 i tak nie ma Performance)
 *   - sesje      -> /envelope/ ✗ stąd autoSessionTracking: false, inaczej wracają błędy CORS
 * Session Replay w 6.x nie istnieje. Przy podniesieniu instancji Sentry: patrz doc/dokumentacja/sentry.md.
 *
 * `Sentry.init({ app })` podpina własny `app.config.errorHandler` - nie przypisuj własnego PO tym
 * wywołaniu, bo nadpisze handler Sentry i wyłączy zbieranie błędów komponentów.
 *
 * @param {object} app instancja aplikacji Vue
 * @param {string} contextName nazwa kontekstu (bramki) - tag zdarzeń
 * @returns {void}
 */
export function initSentry(app, contextName) {
  if (!ToolsClass.isProduction()) {
    return
  }

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY,
    environment: import.meta.env.MODE,
    // tag kontekstu - bez niego 266 bramek zlewa się w Sentry w jeden projekt
    initialScope: {
      tags: { context: contextName },
    },
    ignoreErrors: SENTRY_IGNORE_ERRORS,
    denyUrls: SENTRY_DENY_URLS,
    autoSessionTracking: false,
    beforeSend,
  })
}
